import {
  Loader2,
  Mail,
  MoreHorizontal,
  RefreshCcw,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AuthApiError, useAuth } from "@/lib/auth";
import {
  createInvitation,
  listInvitations,
  revokeInvitation,
  type AdminInvitationRow,
  type CreatedInvitation,
  type InvitationStatus,
} from "@/lib/auth/admin-api";
import { formatAbsoluteDateTime, formatRelativeTime } from "@/lib/format";

import {
  ConfirmUserActionDialog,
  defaultActionErrorMessage,
} from "./confirm-user-action-dialog";
import { InviteUserDialog } from "./invite-user-dialog";

interface ToastState {
  tone: "success";
  message: string;
}

function StatusBadge({ status }: { status: InvitationStatus }) {
  const variant =
    status === "pending"
      ? "default"
      : status === "used"
        ? "success"
        : "outline";
  const label =
    status === "pending" ? "Pending" : status === "used" ? "Accepted" : "Expired";
  return <Badge variant={variant}>{label}</Badge>;
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function resendErrorMessage(err: unknown): string {
  if (err instanceof AuthApiError) {
    if (err.code === "user_exists") {
      return "A user with this email already exists — the original invitation has already been accepted.";
    }
    return err.serverMessage ?? "Couldn't resend the invitation. Please try again.";
  }
  return "Couldn't reach the server. Check your connection and retry.";
}

function revokeErrorMessage(err: unknown): string {
  if (err instanceof AuthApiError) {
    if (err.code === "already_used") {
      return "This invitation has already been accepted and can't be revoked.";
    }
    if (err.code === "not_found") {
      return "That invitation no longer exists.";
    }
    return err.serverMessage ?? "Couldn't revoke the invitation. Please try again.";
  }
  return defaultActionErrorMessage(err);
}

export function InvitationsPanel() {
  const { user: principal } = useAuth();
  const [rows, setRows] = useState<AdminInvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<AdminInvitationRow | null>(
    null,
  );
  const [resendTarget, setResendTarget] = useState<AdminInvitationRow | null>(
    null,
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    listInvitations(controller.signal)
      .then((res) => {
        setRows(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof AuthApiError) {
          setLoadError(err.serverMessage ?? "Couldn't load invitations.");
        } else {
          setLoadError("Couldn't reach the server. Check your connection.");
        }
        setLoading(false);
      });
    return () => controller.abort();
  }, [refetchKey]);

  useEffect(() => {
    if (!toast) return;
    const handle = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(handle);
  }, [toast]);

  const triggerRefetch = useCallback(() => {
    setRefetchKey((k) => k + 1);
  }, []);

  const handleInvited = useCallback((invitation: CreatedInvitation) => {
    setToast({
      tone: "success",
      message: `Invitation sent to ${invitation.email}.`,
    });
    triggerRefetch();
  }, [triggerRefetch]);

  const handleResend = useCallback(async () => {
    if (!resendTarget) return;
    await createInvitation({
      email: resendTarget.email,
      role: resendTarget.role,
    });
  }, [resendTarget]);

  const handleResendSuccess = useCallback(() => {
    if (!resendTarget) return;
    setToast({
      tone: "success",
      message: `Invitation resent to ${resendTarget.email}.`,
    });
    triggerRefetch();
  }, [resendTarget, triggerRefetch]);

  const handleRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    await revokeInvitation(revokeTarget.id);
  }, [revokeTarget]);

  const handleRevokeSuccess = useCallback(() => {
    if (!revokeTarget) return;
    setToast({
      tone: "success",
      message: `Invitation to ${revokeTarget.email} revoked.`,
    });
    triggerRefetch();
  }, [revokeTarget, triggerRefetch]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Pending invitations expire 24 hours after they&apos;re sent. Resend
          to rotate the token without changing the recipient.
        </p>
        <Button size="sm" onClick={() => setInviteOpen(true)}>
          <UserPlus className="h-4 w-4" />
          Invite user
        </Button>
      </div>

      {toast ? (
        <p
          role="status"
          className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success"
        >
          {toast.message}
        </p>
      ) : null}

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Invited by</TableHead>
              <TableHead>Invited</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="w-12 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Loading invitations…
                  </div>
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="space-y-2 py-6 text-center">
                    <p className="text-sm text-destructive">{loadError}</p>
                    <Button size="sm" variant="outline" onClick={triggerRefetch}>
                      Retry
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="space-y-2 py-8 text-center">
                    <Mail
                      className="mx-auto h-5 w-5 text-muted-foreground"
                      aria-hidden
                    />
                    <p className="text-sm text-muted-foreground">
                      No invitations yet.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const invitedByLabel =
                  row.invited_by === principal?.id
                    ? "You"
                    : shortId(row.invited_by);
                const invited = {
                  abs: formatAbsoluteDateTime(row.created_at),
                  rel: formatRelativeTime(row.created_at),
                };
                const expires = {
                  abs: formatAbsoluteDateTime(row.expires_at),
                  rel: formatRelativeTime(row.expires_at),
                };
                return (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">
                      {row.email}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={row.role === "admin" ? "default" : "outline"}
                      >
                        {row.role === "admin" ? "Admin" : "User"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={row.invited_by}
                    >
                      {invitedByLabel}
                    </TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={invited.abs}
                    >
                      {invited.rel}
                    </TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={expires.abs}
                    >
                      {expires.rel}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.status === "pending" ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label={`Actions for invitation to ${row.email}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                setResendTarget(row);
                              }}
                            >
                              <RefreshCcw className="h-4 w-4" />
                              Resend
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                setRevokeTarget(row);
                              }}
                              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                              Revoke
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span aria-hidden className="text-muted-foreground">
                          —
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <InviteUserDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={handleInvited}
      />

      <ConfirmUserActionDialog
        open={resendTarget !== null}
        title="Resend invitation"
        description={
          resendTarget ? (
            <>
              Send a fresh invitation email to{" "}
              <span className="font-mono text-foreground">
                {resendTarget.email}
              </span>
              . The old link stops working and the new one expires in 24 hours.
            </>
          ) : null
        }
        confirmLabel="Resend"
        loadingLabel="Resending…"
        errorMessage={resendErrorMessage}
        onConfirm={handleResend}
        onOpenChange={(open) => {
          if (!open) setResendTarget(null);
        }}
        onSuccess={handleResendSuccess}
      />

      <ConfirmUserActionDialog
        open={revokeTarget !== null}
        title="Revoke invitation"
        description={
          revokeTarget ? (
            <>
              Revoke the invitation to{" "}
              <span className="font-mono text-foreground">
                {revokeTarget.email}
              </span>
              . The email link stops working immediately.
            </>
          ) : null
        }
        confirmLabel="Revoke"
        loadingLabel="Revoking…"
        destructive
        errorMessage={revokeErrorMessage}
        onConfirm={handleRevoke}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
        onSuccess={handleRevokeSuccess}
      />
    </div>
  );
}
